/**
 * 请假申请共用的常量与计算
 *
 * 口径与 web 端 views/h5/crm/leaveApply/LeaveApplyList.vue 里内联的实现一致
 * （那份是页面内自定义的，暂未抽出来；改动任一处都要两边同步）。
 */

/** 请假状态，与后端 LeaveApplyConstant.Status 对齐 */
export const STATUS = {DRAFT: 0, APPROVING: 100, PASSED: 200, REJECTED: 300};
export const STATUS_OPTIONS = [
    {value: STATUS.DRAFT, label: "草稿"},
    {value: STATUS.APPROVING, label: "审批中"},
    {value: STATUS.PASSED, label: "已通过"},
    {value: STATUS.REJECTED, label: "已驳回"},
];
export const STATUS_TAG = {0: "info", 100: "warning", 200: "success", 300: "danger"};

/** 审批状态（工作流维度），与后端 ProcessFlowConstant.ApprovalStatus 对齐 */
export const APPROVAL_OPTIONS = [
    {value: 0, label: "未开始"},
    {value: 100, label: "审批中"},
    {value: 200, label: "已中止"},
    {value: 300, label: "已完成"},
    {value: 400, label: "已撤回"},
    {value: 500, label: "已驳回"},
];

/** 请假时段，与后端 LeaveApplyConstant.TimeType 对齐 */
export const TIME_TYPES = [
    {value: 1, label: "上午"},
    {value: 2, label: "下午"},
];

/** 时长类型是推导结果，仅用于展示与筛选（100整天 200多天 300某天上午 400某天下午） */
export const DURATION_TYPES = [
    {value: 100, label: "整天"},
    {value: 200, label: "多天"},
    {value: 300, label: "某一天上午"},
    {value: 400, label: "某一天下午"},
];

/** 假别标签色，按字典顺序循环取，字典可增删所以不写死映射 */
export const TYPE_COLORS = ["primary", "warning", "danger", "success", "info"];

/** 时段在一天内的序号：上午 0、下午 1 */
const slotOf = (type) => (type === 2 ? 1 : 0);

/**
 * 推导请假天数（天），与后端 LeaveApplyConstant.halfDays 同一公式：
 *   半天数 = (结束日期 - 开始日期) × 2 + (结束时段 - 开始时段) + 1
 * 返回 null 表示起止时间还没填全或非法，此时不展示结果。
 */
export function deriveDays(startDate, startTimeType, endDate, endTimeType) {
    if (!startDate || !endDate || !startTimeType || !endTimeType) {
        return null;
    }
    const s = new Date(String(startDate).slice(0, 10) + "T00:00:00");
    const e = new Date(String(endDate).slice(0, 10) + "T00:00:00");
    if (isNaN(s.getTime()) || isNaN(e.getTime())) {
        return null;
    }
    const dayDiff = Math.round((e - s) / 86400000);
    const halfDays = dayDiff * 2 + slotOf(endTimeType) - slotOf(startTimeType) + 1;
    return halfDays > 0 ? halfDays * 0.5 : null;
}

/** 由起止时间推导时长类型，与后端 LeaveApplyConstant.durationTypeOf 同一口径 */
export function deriveDurationType(startDate, startTimeType, endDate, endTimeType) {
    const s = new Date(String(startDate).slice(0, 10) + "T00:00:00");
    const e = new Date(String(endDate).slice(0, 10) + "T00:00:00");
    if (isNaN(s.getTime()) || isNaN(e.getTime()) || s.getTime() !== e.getTime()) {
        return 200; // 跨天一律「多天」
    }
    const startMorning = slotOf(startTimeType) === 0;
    const endMorning = slotOf(endTimeType) === 0;
    if (startMorning && endMorning) {
        return 300;
    }
    if (!startMorning && !endMorning) {
        return 400;
    }
    return 100;
}

export function statusName(status) {
    const hit = STATUS_OPTIONS.find(item => item.value === status);
    return hit ? hit.label : "—";
}

export function statusTagType(status) {
    return STATUS_TAG[status] || "info";
}

export function approvalName(status) {
    const hit = APPROVAL_OPTIONS.find(item => item.value === status);
    return hit ? hit.label : "未开始";
}

/** 审批状态标签色：进行中橙、完成绿、中止红、未开始灰（驳回也用橙，是需要申请人去改的待办信号） */
export function approvalTagType(status) {
    if (status === 100 || status === 500) {
        return "warning";
    }
    if (status === 300) {
        return "success";
    }
    if (status === 200) {
        return "danger";
    }
    return "info";
}

export function timeTypeName(value) {
    const hit = TIME_TYPES.find(item => item.value === value);
    return hit ? hit.label : "";
}

export function durationTypeName(value) {
    const hit = DURATION_TYPES.find(item => item.value === value);
    return hit ? hit.label : "";
}

/** 起止时间展示：2026-10-08 上午 → 2026-10-09 下午 */
export function dateRangeText(startDate, startTimeType, endDate, endTimeType) {
    const start = startDate ? String(startDate).slice(0, 10) : "—";
    const end = endDate ? String(endDate).slice(0, 10) : "—";
    return start + " " + (timeTypeName(startTimeType) || "") + " → " + end + " " + (timeTypeName(endTimeType) || "");
}
